import { ExpiredPasswordForm, LoginForm, UnloggedPage } from '@sancare/typescript-commons'
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import { sync } from 'vuex-router-sync/dist/vuex-router-sync.esm-bundler'

import EventViewer from '@/maintenance/EventViewer.vue'
import { hasPermission } from '@/misc/permissions/permissionsHelper'
import SmrPage from '@/stay-displayer/smr/SmrPage.vue'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

import AdminPage from './admin/AdminPage.vue'
import AutomationGroupEditor from './automation/AutomationGroupEditor.vue'
import AutomationGroupHistory from './automation/AutomationGroupHistory.vue'
import AutomationStayList from './automation/StayListPage.vue'
import AboutPage from './home/AboutPage.vue'
import HomePage from './home/HomePage.vue'
import EventObserver from './maintenance/EventObserver.vue'
import MaintenanceHome from './maintenance/MaintenanceHome.vue'
import StayHistory from './maintenance/StayHistory.vue'
import ManageApiKeyPage from './manage-api-key/ManageApiKeyPage.vue'
import StatsPage from './stats/StatsPage.vue'
import RhsPage from './stay-displayer/smr/rhs/RhsPage.vue'
import StayDisplayerPage from './stay-displayer/StayDisplayerPage.vue'
import StayListPage from './stay-list/StayListPage.vue'
import store from './store'

const pmsiPrefix = '/:pmsiType(mco|smr)'
function getPmsiType(route) {
  return route.params.pmsiType
}

// Helper to build legacy redirects to the /mco/... prefix for URLs that were pmsiType-agnostic
const legacyToMco = (legacyPath) => ({ path: legacyPath, redirect: `/mco${legacyPath}` })

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      component: UnloggedPage,
      children: [
        {
          path: '',
          name: 'login',
          component: LoginForm,
          props: { ssoLoginUrl: '/api/oidc/login' },
          meta: { requiresAuth: false, unloggedPage: true },
        },
        {
          // This route is used by users to change their password without to be connected (eg: expired password)
          path: 'expired-password',
          name: 'expired-password',
          component: ExpiredPasswordForm,
          meta: { requiresAuth: false, unloggedPage: true },
        },
      ],
    },
    // Backward compat: redirect paths without pmsiType to /mco/...
    { path: '/', redirect: '/mco' },
    legacyToMco('/stay-list'),
    legacyToMco('/maintenance'),
    legacyToMco('/maintenance/event-observer'),
    legacyToMco('/maintenance/event-observer/detail'),
    {
      path: '/maintenance/stay-history/:pmsiId?',
      redirect: (to) => `/mco/maintenance/stay-history${to.params.pmsiId ? `/${to.params.pmsiId}` : ''}`
    },
    legacyToMco('/stats'),
    legacyToMco('/automation/edit-automation-group'),
    legacyToMco('/automation/automation-stay-list'),
    {
      path: '/automation/:automationGroupId/history',
      redirect: (to) => `/mco/automation/${to.params.automationGroupId}/history`
    },
    {
      name: 'home',
      path: pmsiPrefix,
      component: HomePage,
      props: (route) => ({ pmsiType: getPmsiType(route) }),
      meta: { requiresAuth: true }
    },
    {
      name: 'about',
      path: '/about',
      component: AboutPage,
      meta: { requiresAuth: true }
    },
    {
      name: 'staylist',
      path: `${pmsiPrefix}/stay-list`,
      component: StayListPage,
      props: (route) => ({ pmsiType: getPmsiType(route) }),
      meta: { requiresAuth: true },
    },
    {
      name: 'admin',
      path: '/admin',
      component: AdminPage,
      meta: { requiresAuth: true, requireSomeRoles: ['ROLE_USER_MANAGER', 'ROLE_ADMIN_MANAGER', 'ROLE_SETTING_UPDATER'] },
    },
    {
      name: 'maintenance-home',
      path: `${pmsiPrefix}/maintenance`,
      component: MaintenanceHome,
      meta: { requiresAuth: true, requireSomeRoles: ['ROLE_MAINTENANCE_VIEWER'] },
      props: (route) => ({ pmsiType: getPmsiType(route) }),
    },
    {
      name: 'maintenance-event-observer',
      path: `${pmsiPrefix}/maintenance/event-observer`,
      component: EventObserver,
      meta: { requiresAuth: true, requireSomeRoles: ['ROLE_MAINTENANCE_VIEWER'] },
      props: (route) => ({ pmsiType: getPmsiType(route) }),
    },
    {
      name: 'maintenance-event-visualizer',
      path: `${pmsiPrefix}/maintenance/event-observer/detail`,
      component: EventViewer,
      meta: { requiresAuth: true, requireSomeRoles: ['ROLE_MAINTENANCE_VIEWER'] },
      props: (route) => ({ pmsiType: getPmsiType(route) }),
    },
    {
      name: 'maintenance-stay-history',
      path: `${pmsiPrefix}/maintenance/stay-history/:pmsiId?`,
      component: StayHistory,
      meta: {
        requiresAuth: true,
        requireSomeRoles: ['ROLE_MAINTENANCE_VIEWER']
      },
      props: (route) => ({
        pmsiId: route.params.pmsiId ?? null,
        pmsiType: getPmsiType(route),
      })
    },
    {
      name: 'stats',
      path: `${pmsiPrefix}/stats`,
      component: StatsPage,
      meta: { requiresAuth: true }
    },
    {
      name: 'automation-editor',
      path: `${pmsiPrefix}/automation/edit-automation-group`,
      component: AutomationGroupEditor,
      meta: { requiresAuth: true }
    },
    {
      name: 'automation-history',
      path: `${pmsiPrefix}/automation/:automationGroupId/history`,
      component: AutomationGroupHistory,
      meta: { requiresAuth: true }
    },
    {
      name: 'automation-stay-list',
      path: `${pmsiPrefix}/automation/automation-stay-list`,
      component: AutomationStayList,
      meta: { requiresAuth: true }
    },
    {
      name: 'manage-api-key',
      path: '/manage-api-key',
      component: ManageApiKeyPage,
      meta: { requiresAuth: true },
    },
    // redirect old url to mco prefixed ones
    //   caution: order matters, /:idType(...) must come before /:stayId (more specific first),
    //   and both must come after all other named routes to avoid catching unmatched paths prematurely.
    {
      path: '/:idType(administrativelocalstayid|administrativeid|id|sancareid|rssid|legacy)/:stayId',
      redirect: (to) => `/mco/${to.params.idType}/${to.params.stayId}`
    },
    // /:stayId (without idType) assumes numeric id — redirect to /mco/id/:stayId
    { path: '/:stayId', redirect: (to) => `/mco/administrativeid/${to.params.stayId}` },
    {
      name: 'mco-stay',
      path: '/mco/:idType(administrativelocalstayid|administrativeid|id|sancareid|rssid|legacy)/:stayId',
      component: StayDisplayerPage,
      props: (route) => ({
        stayId: route.params.stayId,
        idType: route.params.idType,
      }),
      meta: { requiresAuth: true }
    },
    {
      name: 'smr-stay',
      path: '/smr/:idType(id|smrid)/:stayId',
      component: SmrPage,
      props: (route) => ({
        stayId: route.params.stayId,
        idType: route.params.idType,
      }),
      meta: { requiresAuth: true }
    },
    {
      name: 'rhs',
      path: '/smr/:stayId/rhs/:rhsId',
      component: RhsPage,
      props: (route) => ({
        stayId: Number(route.params.stayId),
        rhsId: Number(route.params.rhsId),
      }),
      meta: { requiresAuth: true }
    }
    // TODO: { path: '*', component: NotFoundComponent },
  ],
})

router.beforeEach(async (to, from, next) => {
  store.commit('toast/resetToast')

  // SSO redirect: the cookie is valid but isAuthenticated isn't, so confirm with the backend before showing the login page.
  if (from === START_LOCATION && to.query.sso && !store.getters.isAuthenticated) {
    await store.dispatch('getSelf')
  }

  // Redirect legacy /ssr/* paths to /smr/*
  if (to.path === '/ssr' || to.path.startsWith('/ssr/')) {
    return next({ ...to, path: to.path.replace(/^\/ssr/, '/smr') })
  }

  if (to.path.includes('/smr') && store.getters.pmsiType !== PmsiTypeEnum.SMR) {
    store.commit('setPmsiType', PmsiTypeEnum.SMR)
  } else if (!to.path.includes('/smr') && store.getters.pmsiType !== PmsiTypeEnum.MCO) {
    store.commit('setPmsiType', PmsiTypeEnum.MCO)
  }
  if (to.query.mode) {
    store.commit('setPmsiMode', to.query.mode)
  } else {
    store.commit('initPmsiMode')
  }

  if (to.name === 'staylist'){
    store.commit('setStayListRoute', to)
  }

  // Pages forbidden to logged-in users
  if (to.matched.some((record) => record.meta.unloggedPage) && store.getters.isAuthenticated) {
    return next({ path: to.query.redirect || '/' })
  }

  // Pages forbidden to unlogged users
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated) {
      const nextPage = { path: '/login' }
      if (to.fullPath !== '/') {
        nextPage.query = { redirect: to.fullPath }
      }
      return next(nextPage)
    }
  }

  // Pages forbidden to non-admins
  let permissionDenied = false
  for (const route of to.matched) {
    if (route.meta.requireSomeRoles) {
      permissionDenied = !route.meta.requireSomeRoles.some((role) => hasPermission(role, store.getters.userRole))
    }
    if (!permissionDenied && route.meta.requireAllRoles) {
      permissionDenied = !route.meta.requireAllRoles.every((role) => hasPermission(role, store.getters.userRole))
    }
    if (!permissionDenied && route.meta.requireCallback) {
      permissionDenied = !route.meta.requireCallback(store.state.login.currentUser)
    }
    if (permissionDenied) {
      break
    }
  }
  if (permissionDenied) {
    return next({ path: '/' })
  }

  // Drop the one-off SSO marker once it has served its purpose, so it doesn't linger in the URL bar.
  if (to.query.sso) {
    const query = { ...to.query }
    delete query.sso
    return next({ ...to, query })
  }

  return next()
})

router.onError(async (error) => {
  await router.push({ path: '/' })
  store.commit('toast/setError', error.message)
})

export function useRouter(callSync) {
  // FIXME awful trick in order to prevent calling sync() in tests
  if (callSync) {
    sync(store, router)
  }

  return router
}

export default router
